import { Component, DestroyRef, effect, EventEmitter, inject, input, Output, ViewChild } from '@angular/core';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { AbstractControl, FormControl, ReactiveFormsModule, TouchedChangeEvent, Validators } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteOrigin,
  MatAutocompleteTrigger,
  MatOption
} from '@angular/material/autocomplete';
import { Party } from '../../models/user-details-form';
import { catchError, debounceTime, distinctUntilChanged, filter, map, NEVER, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PartyService } from '../../services/party.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatError } from '@angular/material/form-field';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIcon } from '@angular/material/icon';
import { CreatePartyComponent } from '../create-party/create-party.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-party-selector',
  standalone: true,
  templateUrl: './party-selector.component.html',
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatAutocompleteOrigin,
    MatProgressSpinner,
    MatError,
    MatIcon
  ],
  styleUrl: './party-selector.component.scss'
})
export class PartySelectorComponent {
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild(MatAutocomplete) autocomplete!: MatAutocomplete;
  relatedControl = input<AbstractControl | null>(null);
  label = input<string>('Select party');
  suggestedParty = input<Party | null>(null);
  disabled = input<boolean>(false);

  @Output() selectedParty = new EventEmitter<Party | null>();

  control = new FormControl<string | Party | null>(null);
  options: Party[] = [];
  isLoading = false;

  private destroyRef = inject(DestroyRef);
  private partyService = inject(PartyService);
  private isTouchTracked = false;
  private matSnackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  constructor() {
    effect(() => {
      this.updateControlValidator();
      this.syncDisabledState();
    });

    effect(() => {
      // Runs whenever suggestedParty() itself changes (i.e. the Main Party actually
      // changed - parties-table.component.ts memoizes it so this doesn't fire on
      // every change-detection tick). Unconditional on purpose: this must never be
      // skippable by a stale control value, otherwise a real Main Party change can
      // leave a previous party's quick-pick option stuck on screen.
      const suggested = this.suggestedParty();
      this.isLoading = false;
      this.control.setValue(null, { emitEvent: false });
      this.options = suggested ? [suggested] : [];
    });
  }

  ngOnInit() {
    this.handleControlValueChange();
  }

  ngAfterViewInit() {
    this.autocompleteTrigger.panelClosingActions
      .pipe(
        tap(event => {
          this.control.setValue(null, { emitEvent: false });
          this.options = this.suggestedOptions();
          this.control.markAsTouched();
          this.selectedParty.emit(event?.source ? event.source.value : null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  displayCustomer(value: string | Party | null): string {
    return typeof value === 'object' && value !== null ? value.name : '';
  }

  onFocus() {
    if (!this.control.value) {
      this.isLoading = false;
      this.options = this.suggestedOptions();
    }
  }

  createParty() {
    this.dialog.open(CreatePartyComponent);
  }

  private handleControlValueChange() {
    this.control.valueChanges
      .pipe(
        tap(() => {
          this.isLoading = true;
          this.options = [];
        }),
        map(value => (typeof value === 'string' ? value.trim() : (value?.name ?? ''))),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(term => {
          if (term.length < 3) {
            this.isLoading = false;
            this.options = term.length === 0 ? this.suggestedOptions() : [];
            return of([]);
          }
          return this.fetchCustomers(term).pipe(
            tap(options => {
              this.options = options;
              this.isLoading = false;
            }),
            catchError((error: HttpErrorResponse) => {
              console.error(error);
              this.isLoading = false;
              this.matSnackBar.open('Failed to fetch parties. Please try again later.', 'x');
              return NEVER;
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private fetchCustomers(term: string) {
    return this.partyService.getParties(term);
  }

  private suggestedOptions(): Party[] {
    const party = this.suggestedParty();
    return party ? [party] : [];
  }

  private syncDisabledState() {
    if (this.disabled()) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }

  private updateControlValidator() {
    this.trackControlTouch();
    this.toggleRequiredValidator();
    // emitEvent: false - this only recomputes validity/status (read directly by the
    // template via hasError()/touched), it must not re-emit valueChanges. A real
    // emission here restarts the search debounce and can leave isLoading stuck true
    // if a later identical term gets swallowed by distinctUntilChanged downstream.
    this.control.updateValueAndValidity({ emitEvent: false });
  }

  private toggleRequiredValidator() {
    if (this.relatedControl()?.hasValidator(Validators.required)) {
      this.control.addValidators(Validators.required);
    } else {
      this.control.removeValidators(Validators.required);
    }
  }

  private trackControlTouch() {
    const related = this.relatedControl();
    if (!related || this.isTouchTracked) return;

    this.isTouchTracked = true;
    related.events
      .pipe(
        filter(event => event instanceof TouchedChangeEvent),
        tap(event => {
          event.touched ? this.control.markAsTouched() : this.control.markAsUntouched();
          this.control.updateValueAndValidity({ emitEvent: false });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
